'use client'

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

// baby: Shared light/dark theme for all 8-bit marketing landings (NIN, NINXL, …)
export type Ninxl8BitTheme = 'light' | 'dark'

const STORAGE_KEY = 'ninxl-8bit-theme'
const DEFAULT_THEME: Ninxl8BitTheme = 'light'

type Ninxl8BitThemeContextValue = {
  theme: Ninxl8BitTheme
  setTheme: (theme: Ninxl8BitTheme) => void
  toggleTheme: () => void
}

const Ninxl8BitThemeContext = createContext<Ninxl8BitThemeContextValue | null>(null)

/** baby: Read saved visitor preference before first paint when possible */
const readStoredTheme = (): Ninxl8BitTheme => {
  if (typeof window === 'undefined') return DEFAULT_THEME
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return stored === 'dark' || stored === 'light' ? stored : DEFAULT_THEME
}

export const Ninxl8BitThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<Ninxl8BitTheme>(DEFAULT_THEME)

  // baby: Hydrate from localStorage on mount
  useLayoutEffect(() => {
    setThemeState(readStoredTheme())
  }, [])

  const setTheme = useCallback((next: Ninxl8BitTheme) => {
    setThemeState(next)
    window.localStorage.setItem(STORAGE_KEY, next)
  }, [])

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light')
  }, [setTheme, theme])

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  )

  return (
    <Ninxl8BitThemeContext.Provider value={value}>{children}</Ninxl8BitThemeContext.Provider>
  )
}

export const useNinxl8BitTheme = (): Ninxl8BitThemeContextValue => {
  const context = useContext(Ninxl8BitThemeContext)
  if (!context) {
    throw new Error('useNinxl8BitTheme must be used within Ninxl8BitThemeProvider')
  }
  return context
}

/** baby: Safe read for components that also render in tests */
export const useNinxl8BitThemeMode = (): Ninxl8BitTheme => {
  return useContext(Ninxl8BitThemeContext)?.theme ?? DEFAULT_THEME
}
